import type { HoverEvents, PressEvent } from "@react-types/shared";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols/index.d.ts";
import { type CSSProperties, forwardRef, type ReactElement, useMemo } from "react";
import type { AriaButtonProps } from "react-aria";
import { FocusRing, mergeProps, useButton, useHover } from "react-aria";

import { useDOMRef, useTooltip, useVisuallyLoading } from "../../hooks.ts";
import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import style from "../ActionButton/Buttons.module.css";
import { LoadingIcon } from "../ActionButton/LoadingIcon.tsx";
import { useAsyncLoading } from "../ActionButton/useAsyncLoading.ts";
import { Badge, type BadgeProps } from "../Badge/Badge.tsx";
import { ColoredBullet } from "../ColoredLabel/ColoredLabel.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { LoadingSpinnerIntent } from "../ProgressSpinner/settings.ts";
import { AnimatedTooltip } from "../Tooltip/AnimatedTooltip.tsx";
import { TooltipIntent } from "../Tooltip/settings.ts";
import { TriggerButtonIntent, TriggerButtonSize } from "./settings.ts";

interface TriggerButtonTooltipProps {
  intent: TooltipIntent;
  message: ReactElement | string;
}

export interface TriggerButtonProps extends Omit<AriaButtonProps, "isDisabled">, HoverEvents {
  /** No Primary intent */
  intent?: TriggerButtonIntent;
  icon: ReactElement | MaterialSymbol;
  size?: TriggerButtonSize;
  label: string;
  shortcut?: string;
  className?: string;
  /**
   * In case the returned value is a Promise, the loading state will be automatically set
   * as long as the Promise is pending.
   */
  onPress?: (event: PressEvent) => unknown;
  tooltip?: string | TriggerButtonTooltipProps;
  isDisabled?: boolean | string | ReactElement; // can contain the disabled tooltip
  style?: CSSProperties;
  showLabel?: boolean;
  badge?: string | number | boolean;
  badgeIntent?: BadgeProps["intent"];
  dataAttributes?: DOMStringMap;
  isLoading?: boolean;
}

export const TriggerButton = forwardRef<HTMLButtonElement, TriggerButtonProps>(function TriggerButton(props, ref) {
  const triggerRef = useDOMRef<HTMLButtonElement>(ref);

  const { isAsyncLoading, handlePress } = useAsyncLoading(props.onPress);
  const isLoading = useMemo(() => !!props.isLoading || isAsyncLoading, [isAsyncLoading, props.isLoading]);
  const isVisuallyLoading = useVisuallyLoading(isLoading);

  const isDisabled = useMemo(() => !!props.isDisabled || isVisuallyLoading, [isVisuallyLoading, props.isDisabled]);

  const isDisabledIsStringOrElement = typeof props.isDisabled === "string" || typeof props.isDisabled === "object";

  const { buttonProps, isPressed } = useButton(
    { ...props, "aria-label": props["aria-label"] ?? props.label, isDisabled, onPress: handlePress },
    triggerRef,
  );
  const { hoverProps, isHovered } = useHover({ ...props, isDisabled });

  const { triggerProps, tooltipProps, state } = useTooltip(triggerRef, {
    // Do not show a tooltip if neither a tooltip nor a label (if present, do not display it if `showLabel`
    // is set to `true` as it is just repeating the label) is provided. Additionally, do not show a tooltip
    // if `isDisabled` is a boolean or undefined.
    isDisabled: !props.tooltip && !isDisabledIsStringOrElement && (!props.label || !!props.showLabel),
    closeDelay: 0,
  });

  const tooltip: TriggerButtonTooltipProps | undefined =
    props.tooltip && typeof props.tooltip === "string"
      ? {
          intent: props.intent === TriggerButtonIntent.Danger ? TooltipIntent.Danger : TooltipIntent.Default,
          message: props.tooltip,
        }
      : (props.tooltip as TriggerButtonTooltipProps | undefined);

  const dataAttributes = useDataAttributes(props);

  const showLabel = props.showLabel && (props.size === TriggerButtonSize.Normal || props.size == null);

  return (
    <>
      <FocusRing focusRingClass={style.focus}>
        <button
          type="button"
          className={clsx(
            style.trigger_button,
            {
              [style.button_secondary]: props.intent == null || props.intent === TriggerButtonIntent.Secondary,
              [style.button_outlined]: props.intent === TriggerButtonIntent.Outlined,
              [style.button_danger_trigger]: props.intent === TriggerButtonIntent.Danger,
              [style.button_selected]: props.intent === TriggerButtonIntent.Active,
              [style.pressed]: isPressed,
              [style.hover]: isHovered,
              [style.disabled]: props.isDisabled, // while loading the styling remains the same
              [style.small]: props.size === TriggerButtonSize.Small,
              [style.mini]: props.size === TriggerButtonSize.Mini,
              [style.with_label]: showLabel,
            },
            props.className,
          )}
          {...mergeProps(triggerProps, hoverProps, buttonProps)}
          ref={triggerRef}
          data-intent={props.intent}
          data-e2e-label={props["aria-label"] ?? props.label}
          {...dataAttributes}
          data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}trigger-button`}
          style={props.style}
        >
          {isVisuallyLoading && (
            <LoadingIcon
              intent={
                props.isDisabled
                  ? LoadingSpinnerIntent.Secondary
                  : props.intent === TriggerButtonIntent.Active
                    ? LoadingSpinnerIntent.Primary
                    : props.intent === TriggerButtonIntent.Danger
                      ? LoadingSpinnerIntent.Danger
                      : LoadingSpinnerIntent.Secondary
              }
            />
          )}
          <Icon
            icon={props.icon}
            className={clsx(style.trigger_icon, { [style.text_transparent]: isVisuallyLoading })}
          />
          {!!showLabel && (
            <span className={clsx(style.label, { [style.text_transparent]: isVisuallyLoading })}>
              {props.label || props.children}
            </span>
          )}
          {props.badge === true ? (
            <ColoredBullet
              color={props.badgeIntent === "danger" ? "var(--danger-main)" : "var(--primary-main)"}
              className={style.bullet}
            />
          ) : (
            // Empty string and `0` does NOT show a Badge. That's ok.
            !!props.badge && <Badge label={props.badge} className={style.badge} intent={props.badgeIntent} />
          )}
        </button>
      </FocusRing>
      <AnimatedTooltip
        targetRef={triggerRef}
        shortcut={props.shortcut}
        title={props.label}
        intent={isDisabled ? TooltipIntent.Info : tooltip?.intent}
        state={state}
        domProps={tooltipProps}
        nonInteractive
      >
        {isDisabledIsStringOrElement ? props.isDisabled : tooltip?.message}
      </AnimatedTooltip>
    </>
  );
});
